import { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { PillarsSection, ApproachSteps } from "../components/sections";
import { getPage, getValues, mediaUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";

/* Generic About subpage: who-we-are | mission-and-values | why-lemur-quality | our-approach */
export default function AboutSubpage({ slug }) {
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  const [values, setValues] = useState([]);

  useEffect(() => {
    getPage(slug).then(setPage).catch(() => {});
    if (slug === "mission-and-values") getValues().then(setValues).catch(() => {});
  }, [slug]);

  return (
    <div data-testid={`about-${slug}-page`}>
      <PageHeader eyebrow={page?.eyebrow} title={page?.title || ""} lead={page?.lead} crumb={page?.title} />

      {/* Text sections */}
      {page?.sections?.length > 0 && (
        <section className="section bg-paper">
          <div className="wrap">
            {page.sections.map((sec, i) => (
              <Reveal key={i} className="d-lead" style={{ marginBottom: i < page.sections.length - 1 ? 40 : 0 }}>
                {sec.heading && <><span className="eyebrow">{String(i + 1).padStart(2, "0")}</span><h2>{sec.heading}</h2></>}
                {sec.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Team photo (Who We Are) */}
      {slug === "who-we-are" && settings?.about_photo_media_id && (
        <section className="section bg-mist" data-testid="about-photo-section">
          <div className="wrap">
            <Reveal>
              <figure className="photo-card wide">
                <img src={mediaUrl(settings.about_photo_media_id)} alt={settings.about_photo_alt || ""} loading="lazy" />
              </figure>
            </Reveal>
          </div>
        </section>
      )}

      {/* Mission + Values */}
      {slug === "mission-and-values" && (
        <>
          <section className="section bg-paper">
            <div className="wrap">
              <Reveal className="mission" style={{ padding: "44px 40px" }}>
                <span className="eyebrow">Our mission</span>
                <h3 style={{ fontSize: "1.6rem" }}>{page?.mission_title}</h3>
                <p style={{ fontSize: "1.08rem" }}>{page?.mission_text}</p>
              </Reveal>
            </div>
          </section>
          <section className="section bg-mist">
            <div className="wrap">
              <Reveal className="sec-head"><span className="eyebrow">Our values</span><h2>The principles we uphold</h2><p>Grounded in quality, compliance, and the integrity of our results.</p></Reveal>
              <div className="value-grid">
                {values.map((v, i) => (
                  <Reveal key={v.id} className="value-card" delay={(i % 3) * 60} data-testid={`value-${v.order}`}>
                    <span className="vi">{String(v.order).padStart(2, "0")}</span>
                    <h3>{v.title}</h3>
                    <p>{v.description}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Why pillars */}
      {slug === "why-lemur-quality" && (
        <section className="section dark-sec">
          <div className="wrap">
            <Reveal className="sec-head"><span className="eyebrow light">Our commitment</span><h2>What sets us apart</h2><p>We combine technical depth, international alignment, and a commitment to building lasting local capacity.</p></Reveal>
            <PillarsSection />
          </div>
        </section>
      )}

      {/* Approach steps */}
      {slug === "our-approach" && (
        <section className="section bg-mist">
          <div className="wrap">
            <Reveal className="sec-head"><span className="eyebrow">Our methodology</span><h2>Four steps, one standard of rigor</h2><p>From assessment to continuous improvement, we embed compliance and quality into how your organization operates.</p></Reveal>
            <ApproachSteps />
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
